import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import isPermissionEnabled from "common/isPermissionEnabled"
import {
  buildDeliveryPartnerPermissions,
  getRiderProfileSubPermissions,
} from "utils/deliveryPartnerPermissionUtils"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import { useTranslation } from "react-i18next"
import {
  fetchPartners,
  fetchPartnerDetails,
  updatePartnerStatus,
  createPartner,
  updatePartnerProfile,
  clearSelectedPartner,
  setSelectedPartner,
  settleCOD,
  selectPartners,
  selectPartnerLoading,
  selectPagination,
  selectSelectedPartner,
} from "store/slices/deliveryPartnerSlice"
import { showAlert } from "store/reducers/alertReducer"
import { getAllBranchs } from "store/actions/usersAction"
import { getCrossModuleFilterPermissions } from "utils/crossModuleFilterPermissions"
import DeliveryPartnersList from "components/DeliveryPartners/DeliveryPartnersList"
import DeliveryPartnerDetails from "components/DeliveryPartners/DeliveryPartnerDetails"
import DeliveryPartnerDailySummary from "components/DeliveryPartners/DeliveryPartnerDailySummary"
import PartnerStatusModal from "components/DeliveryPartners/PartnerStatusModal"
import SettlementModal from "components/DeliveryPartners/SettlementModal"
import PartnerFormModal from "components/DeliveryPartners/PartnerFormModal"
import Filters from "components/Common/Filters"
import Paginations from "components/Common/Paginations"
import {
  canViewRiderProfileTab,
  getFirstAllowedRiderProfileTab,
} from "utils/riderProfileTabPermissions"

const DeliveryPartnersPage = () => {
  const { t, i18n } = useTranslation()
  document.title = t("Riders ")

  const { id: routePartnerId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const partners = useSelector(selectPartners)
  const loading = useSelector(selectPartnerLoading)
  const pagination = useSelector(selectPagination)
  const selectedPartner = useSelector(selectSelectedPartner)
  const { branchsList } = useSelector(state => state.Users)
  const { userRole, accountType, accountScope } = useSelector(state => state.Login || {})
  const { canFilterByBranch } = getCrossModuleFilterPermissions(
    userRole,
    accountType,
    accountScope
  )

  const riderProfileSubPermissions = useMemo(
    () => getRiderProfileSubPermissions(userRole),
    [userRole]
  )

  const ridersPermissions = useMemo(
    () => buildDeliveryPartnerPermissions(userRole),
    [userRole]
  )

  const actionPermissions = useMemo(
    () => ({
      addUpdate: isPermissionEnabled(
        userRole,
        47,
        "delivery_partners_2",
        accountType,
        accountScope
      ),
      delete: isPermissionEnabled(
        userRole,
        47,
        "delivery_partners_3",
        accountType,
        accountScope
      ),
    }),
    [userRole, accountType, accountScope]
  )

  const mergedRidersPermissions = useMemo(
    () => ({
      ...ridersPermissions,
      addUpdate: ridersPermissions.addUpdate || actionPermissions.addUpdate,
      delete: ridersPermissions.delete || actionPermissions.delete,
    }),
    [ridersPermissions, actionPermissions]
  )

  const tabPermissionContext = useMemo(
    () => ({
      hasView: mergedRidersPermissions.view,
      subPermissions: riderProfileSubPermissions,
      legacyPermissions: mergedRidersPermissions,
    }),
    [mergedRidersPermissions, riderProfileSubPermissions]
  )

  const canViewProfileTab = useCallback(
    tabId => canViewRiderProfileTab({ ...tabPermissionContext, tabId }),
    [tabPermissionContext]
  )

  const [searchValue, setSearchValue] = useState("")
  const [sortBy, setSortBy] = useState("")
  const [sortOrder, setSortOrder] = useState("asc")
  const [selectedFilters, setSelectedFilters] = useState({
    status: "all",
    branch: { label: "All Branches", value: "all" },
    minRating: "all",
    availability: "all",
    sortOrder: "asc",
  })
  const [appliedFilters, setAppliedFilters] = useState({})

  const [detailsModal, setDetailsModal] = useState(false)
  const [detailsPartnerId, setDetailsPartnerId] = useState(null)
  const [detailsInitialTab, setDetailsInitialTab] = useState("overview")
  const [dailySummaryModal, setDailySummaryModal] = useState({
    isOpen: false,
    partnerId: null,
    partner: null,
  })
  const [statusModal, setStatusModal] = useState({
    isOpen: false,
    partner: null,
    isSubmitting: false,
  })
  const [settlementModal, setSettlementModal] = useState({ isOpen: false, partner: null })
  const [partnerFormModal, setPartnerFormModal] = useState({
    isOpen: false,
    mode: "create",
    partner: null,
  })

  const buildParams = useCallback(
    (overrides = {}) => ({
      page: pagination.currentPage,
      limit: pagination.itemsPerPage,
      search: searchValue || undefined,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || "asc",
      ...appliedFilters,
      ...overrides,
    }),
    [pagination.currentPage, pagination.itemsPerPage, searchValue, sortBy, sortOrder, appliedFilters]
  )

  useEffect(() => {
    if (canFilterByBranch) {
      dispatch(getAllBranchs())
    }
  }, [canFilterByBranch, dispatch])

  useEffect(() => {
    if (!mergedRidersPermissions.view) {
      navigate("/dashboard")
    }
  }, [mergedRidersPermissions.view, navigate])

  useEffect(() => {
    dispatch(
      fetchPartners({
        page: 1,
        limit: pagination.itemsPerPage,
        sortOrder: "asc",
      })
    )
  }, [dispatch])

  const branchFilterOptions = useMemo(() => {
    const options = (branchsList || []).map(item => ({
      label: i18n.language === "sa" ? item.nameAr || item.name : item.name,
      value: item._id,
    }))
    return [{ label: t("All Branches"), value: "all" }, ...options]
  }, [branchsList, i18n.language, t])

  const allBranchesOption = useMemo(
    () => branchFilterOptions[0] || { label: t("All Branches"), value: "all" },
    [branchFilterOptions, t]
  )

  useEffect(() => {
    setSelectedFilters(prev => {
      const currentValue = prev.branch?.value ?? prev.branch
      const matchedBranch =
        branchFilterOptions.find(option => option.value === currentValue) ||
        allBranchesOption
      if (prev.branch?.value === matchedBranch.value && prev.branch?.label === matchedBranch.label) {
        return prev
      }
      return { ...prev, branch: matchedBranch }
    })
  }, [branchFilterOptions, allBranchesOption])

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All Status"), value: "all" },
        { label: t("Pending"), value: "Pending" },
        { label: t("Approved"), value: "Approved" },
        { label: t("Rejected"), value: "Rejected" },
        { label: t("Suspended"), value: "Suspended" },
      ],
      placeholder: t("Select Status"),
    },
    ...(canFilterByBranch
      ? [
          {
            name: "branch",
            type: "singleSelect",
            options: branchFilterOptions,
            placeholder: t("Select Branch"),
            isSearchable: true,
          },
        ]
      : []),
    {
      name: "minRating",
      type: "select",
      options: [
        { label: t("All Ratings"), value: "all" },
        { label: "4+", value: "4" },
        { label: "3+", value: "3" },
        { label: "2+", value: "2" },
      ],
      placeholder: t("Select Min Rating"),
    },
    {
      name: "availability",
      type: "select",
      options: [
        { label: t("All"), value: "all" },
        { label: t("Available"), value: "available" },
        { label: t("Not Available"), value: "unavailable" },
        { label: t("On Delivery"), value: "on_delivery" },
      ],
      placeholder: t("Select Availability"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: "asc" },
        { label: t("Descending"), value: "desc" },
      ],
      placeholder: t("Select Sort Order"),
    },
  ]

  const translateMessage = message => {
    if (!message) return ""
    return t(message, { defaultValue: message })
  }

  const handleSearch = () => {
    const nextSortOrder = selectedFilters.sortOrder || "asc"
    setSortOrder(nextSortOrder)
    const nextAppliedFilters = {
      status:
        selectedFilters.status && selectedFilters.status !== "all"
          ? selectedFilters.status
          : undefined,
      branchId:
        canFilterByBranch &&
        selectedFilters.branch?.value &&
        selectedFilters.branch.value !== "all"
          ? selectedFilters.branch.value
          : undefined,
      rating:
        selectedFilters.minRating && selectedFilters.minRating !== "all"
          ? Number(selectedFilters.minRating)
          : undefined,
      availabilityFilter:
        selectedFilters.availability && selectedFilters.availability !== "all"
          ? selectedFilters.availability
          : undefined,
    }
    setAppliedFilters(nextAppliedFilters)
    dispatch(
      fetchPartners({
        page: 1,
        limit: pagination.itemsPerPage,
        search: searchValue || undefined,
        sortBy: sortBy || undefined,
        sortOrder: nextSortOrder,
        ...nextAppliedFilters,
      })
    )
  }

  const handleSort = (field, order) => {
    setSortBy(field)
    setSortOrder(order)
    dispatch(fetchPartners(buildParams({ sortBy: field, sortOrder: order, page: 1 })))
  }

  const handleFilterChange = (value, filterName) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterName]: value,
    }))
  }

  const handleResetFilters = () => {
    setSearchValue("")
    setSortOrder("asc")
    setSortBy("")
    setAppliedFilters({})
    setSelectedFilters({
      status: "all",
      branch: allBranchesOption,
      minRating: "all",
      availability: "all",
      sortOrder: "asc",
    })
    dispatch(
      fetchPartners({
        page: 1,
        limit: pagination.itemsPerPage,
        sortOrder: "asc",
      })
    )
  }

  const handlePageChange = page => {
    dispatch(fetchPartners(buildParams({ page })))
  }

  const handleViewDetails = (partner, tab = "overview") => {
    const allowedTab = canViewProfileTab(tab)
      ? tab
      : getFirstAllowedRiderProfileTab(tabPermissionContext)
    const partnerId = partner?._id || partner?.id
    dispatch(setSelectedPartner(partner))
    setDetailsPartnerId(partnerId || null)
    setDetailsInitialTab(allowedTab)
    setDetailsModal(true)
    if (partnerId && routePartnerId !== String(partnerId)) {
      navigate(`/delivery-partners/${partnerId}`, { replace: true })
    }
  }

  const handleCloseDetails = () => {
    setDetailsModal(false)
    setDetailsPartnerId(null)
    setDetailsInitialTab("overview")
    dispatch(clearSelectedPartner())
    if (routePartnerId) {
      navigate("/delivery-partners", { replace: true })
    }
  }

  const handleDailySummary = partner => {
    const partnerId = partner?._id || partner?.id
    if (!partnerId) return
    setDailySummaryModal({
      isOpen: true,
      partnerId,
      partner,
    })
  }

  const handleCloseDailySummary = () => {
    setDailySummaryModal({
      isOpen: false,
      partnerId: null,
      partner: null,
    })
  }

  useEffect(() => {
    if (!routePartnerId) return
    setDetailsPartnerId(routePartnerId)
    setDetailsInitialTab("overview")
    setDetailsModal(true)
    dispatch(fetchPartnerDetails(routePartnerId))
  }, [routePartnerId, dispatch])

  const handleViewPendingOrders = partner => {
    setStatusModal({ isOpen: false, partner: null, isSubmitting: false })
    setPartnerFormModal({ isOpen: false, mode: "create", partner: null })
    handleViewDetails(partner, "orders")
  }

  const handleUpdateStatus = (partner, defaultStatus) => {
    if (!mergedRidersPermissions.addUpdate) return
    setStatusModal({ isOpen: true, partner, defaultStatus, isSubmitting: false })
  }

  const handleOpenCreatePartner = () => {
    if (!mergedRidersPermissions.addUpdate) return
    setPartnerFormModal({ isOpen: true, mode: "create", partner: null })
  }

  const handleOpenEditPartner = partner => {
    if (!mergedRidersPermissions.addUpdate) return
    setPartnerFormModal({ isOpen: true, mode: "edit", partner })
  }

  const handleClosePartnerForm = () => {
    setPartnerFormModal({ isOpen: false, mode: "create", partner: null })
  }

  const handleSubmitPartnerForm = async payload => {
    if (!mergedRidersPermissions.addUpdate) return
    const isEditMode = partnerFormModal.mode === "edit"
    const selectedId = partnerFormModal.partner?._id || partnerFormModal.partner?.id
    const result = isEditMode
      ? await dispatch(updatePartnerProfile({ id: selectedId, payload }))
      : await dispatch(createPartner(payload))

    if (result.type.endsWith("/fulfilled")) {
      dispatch(
        showAlert({
          text: isEditMode
            ? t("Rider updated successfully")
            : t("Rider created successfully"),
          type: "success",
        })
      )
      handleClosePartnerForm()
      dispatch(fetchPartners(buildParams()))
    } else {
      dispatch(
        showAlert({
          text: translateMessage(result.payload) ||
            (isEditMode ? t("Failed to update rider") : t("Failed to create rider")),
          type: "error",
        })
      )
    }
  }

  const handleStatusConfirm = async (status, reason) => {
    if (!mergedRidersPermissions.addUpdate) return
    const { partner } = statusModal
    setStatusModal(prev => ({ ...prev, isSubmitting: true }))
    const result = await dispatch(
      updatePartnerStatus({ id: partner._id || partner.id, status, reason })
    )
    if (updatePartnerStatus.fulfilled.match(result)) {
      dispatch(
        showAlert({
          text: t("Rider status updated to {{status}}", { status: t(status) }),
          type: "success",
        })
      )
      dispatch(fetchPartners(buildParams()))
      if (detailsModal && detailsPartnerId) {
        dispatch(fetchPartnerDetails(detailsPartnerId))
      }
    } else {
      dispatch(
        showAlert({
          text: translateMessage(result.payload) || t("Failed to update status"),
          type: "error",
        })
      )
    }
    setStatusModal({ isOpen: false, partner: null, isSubmitting: false })
  }

  const handleVerifyDocuments = partner => {
    if (!mergedRidersPermissions.documents || !canViewProfileTab("documents")) return
    handleViewDetails(partner, "documents")
  }

  const handleSettleCOD = () => {
    if (!mergedRidersPermissions.earnings) return
    setSettlementModal({ isOpen: true, partner: selectedPartner })
  }

  const handleSettlementConfirm = async (amount, date) => {
    if (!mergedRidersPermissions.earnings) return
    const partner = settlementModal.partner || selectedPartner
    setSettlementModal({ isOpen: false, partner: null })
    const result = await dispatch(
      settleCOD({ id: partner._id || partner.id, amount, date })
    )
    if (settleCOD.fulfilled.match(result)) {
      dispatch(showAlert({ text: t("COD settlement successful"), type: "success" }))
    } else {
      dispatch(
        showAlert({
          text: translateMessage(result.payload) || t("Settlement failed"),
          type: "error",
        })
      )
    }
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-3 align-items-center">
          <Col>
            <h4 className="mb-0 fw-semibold">{t("Riders")}</h4>
          </Col>
          {mergedRidersPermissions.addUpdate ? (
            <Col xs="auto">
              <Button className="btn btn-primary" onClick={handleOpenCreatePartner}>
                <i className="bx bx-plus me-1"></i>{t("Add Rider")}
              </Button>
            </Col>
          ) : null}
        </Row>

        <Row>
          <Col md={12}>
            <Card className="shadow-sm">
              <CardBody>
                <Filters
                  filters={filters}
                  searchTerm={searchValue}
                  handleSearchChange={e => setSearchValue(e.target.value)}
                  handleFilterChange={handleFilterChange}
                  handleSearch={handleSearch}
                  onReset={handleResetFilters}
                  selectedFilters={selectedFilters}
                  searchBy={t("Search by name, phone")}
                />
                <DeliveryPartnersList
                  partners={partners}
                  loading={loading.list}
                  permissions={{
                    ...mergedRidersPermissions,
                    canViewDocumentsTab: canViewProfileTab("documents"),
                    canViewEarningsTab: canViewProfileTab("earnings"),
                  }}
                  onViewDetails={handleViewDetails}
                  onDailySummary={handleDailySummary}
                  onVerifyDocuments={handleVerifyDocuments}
                  onUpdateStatus={handleUpdateStatus}
                  onEditPartner={handleOpenEditPartner}
                  onSort={handleSort}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  currentPage={pagination.currentPage}
                  itemsPerPage={pagination.itemsPerPage}
                />
                {!loading.list && (
                  <div className="mt-3">
                    <Paginations
                      data={partners}
                      totalCount={pagination.totalCount}
                      currentPage={pagination.currentPage}
                      totalPages={pagination.totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={detailsModal}
        toggle={handleCloseDetails}
        size="xl"
        scrollable
      >
        <ModalHeader toggle={handleCloseDetails}>{t("Rider Details")}</ModalHeader>
        <ModalBody>
          {detailsPartnerId && (
            <DeliveryPartnerDetails
              partnerId={detailsPartnerId}
              initialTab={detailsInitialTab}
              permissions={mergedRidersPermissions}
              profileSubPermissions={riderProfileSubPermissions}
              onSettleCOD={handleSettleCOD}
              codData={selectedPartner?.cod}
              codLoading={loading.cod}
            />
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={dailySummaryModal.isOpen}
        toggle={handleCloseDailySummary}
        size="xl"
        scrollable
        style={{ maxWidth: "95vw", width: "95vw" }}
      >
        <ModalHeader toggle={handleCloseDailySummary}>
          {t("Daily Summary")}
        </ModalHeader>
        <ModalBody>
          {dailySummaryModal.partnerId ? (
            <DeliveryPartnerDailySummary
              partnerId={dailySummaryModal.partnerId}
              initialPartner={dailySummaryModal.partner}
            />
          ) : null}
        </ModalBody>
      </Modal>

      <PartnerStatusModal
        isOpen={statusModal.isOpen}
        partner={statusModal.partner}
        toggle={() =>
          setStatusModal(prev =>
            prev.isSubmitting ? prev : { isOpen: false, partner: null, isSubmitting: false }
          )
        }
        currentStatus={statusModal.defaultStatus || statusModal.partner?.status}
        onConfirm={handleStatusConfirm}
        onViewPendingOrders={handleViewPendingOrders}
        loading={statusModal.isSubmitting}
      />

      <SettlementModal
        isOpen={settlementModal.isOpen}
        toggle={() => setSettlementModal({ isOpen: false, partner: null })}
        pendingAmount={
          selectedPartner?.cod?.outstandingAmount ?? 0
        }
        onConfirm={handleSettlementConfirm}
      />

      <PartnerFormModal
        isOpen={partnerFormModal.isOpen}
        mode={partnerFormModal.mode}
        partner={partnerFormModal.partner}
        toggle={handleClosePartnerForm}
        onSubmit={handleSubmitPartnerForm}
        onViewPendingOrders={handleViewPendingOrders}
        loading={loading.status}
      />
    </div>
  )
}

export default DeliveryPartnersPage
